/**
 * <countdown-timer> — Shared countdown Web Component.
 *
 * Counts down to the ISO 8601 date in the `expires-at` attribute.
 * Updates child elements bearing [data-timer-days], [data-timer-hours],
 * [data-timer-minutes], [data-timer-seconds] with zero-padded values.
 *
 * Attributes:
 *   expires-at            — ISO 8601 timestamp (e.g. "2025-12-31T23:59:59+05:30")
 *   expiration-behavior   — "hide" to hide the element when the timer reaches zero
 *
 * Used by cart-timer.liquid and countdown section(s).
 */

class CountdownTimer extends HTMLElement {
  connectedCallback() {
    const raw = this.getAttribute('expires-at');
    if (!raw) return;

    this.expiresAt = new Date(raw);
    if (Number.isNaN(this.expiresAt.getTime())) return;

    this.daysEl = this.querySelector('[data-timer-days]');
    this.hoursEl = this.querySelector('[data-timer-hours]');
    this.minutesEl = this.querySelector('[data-timer-minutes]');
    this.secondsEl = this.querySelector('[data-timer-seconds]');

    this.tick();
    this.intervalId = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    this.clearTimer();
  }

  tick() {
    const remaining = Math.max(0, Math.floor((this.expiresAt.getTime() - Date.now()) / 1000));

    if (remaining <= 0) {
      this.onExpired();
      return;
    }

    const days = Math.floor(remaining / 86400);
    const hours = Math.floor((remaining % 86400) / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;

    const pad = (n) => String(n).padStart(2, '0');

    if (this.daysEl) this.daysEl.textContent = pad(days);
    if (this.hoursEl) this.hoursEl.textContent = pad(hours);
    if (this.minutesEl) this.minutesEl.textContent = pad(minutes);
    if (this.secondsEl) this.secondsEl.textContent = pad(seconds);
  }

  onExpired() {
    this.clearTimer();

    /* Zero-out the display */
    const pad = () => '00';
    if (this.daysEl) this.daysEl.textContent = pad();
    if (this.hoursEl) this.hoursEl.textContent = pad();
    if (this.minutesEl) this.minutesEl.textContent = pad();
    if (this.secondsEl) this.secondsEl.textContent = pad();

    if (this.getAttribute('expiration-behavior') === 'hide') {
      this.classList.add('hidden');
    }
  }

  clearTimer() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
